import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-(--button-radius) text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:w-(--button-icon-size) [&_svg]:h-(--button-icon-size) [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-(--button-primary) text-primary-foreground shadow-(--button-shadow) hover:enabled:bg-(--button-primary-hover)',
        destructive:
          'bg-destructive text-destructive-foreground hover:enabled:bg-destructive/90',
        outline:
          'border-2 border-(--button-outline-border) bg-transparent text-(--button-outline-text) font-semibold hover:enabled:bg-accent/50 shadow-none',
        secondary:
          'bg-secondary text-secondary-foreground hover:enabled:bg-secondary/80',
        ghost: 'bg-transparent text-foreground hover:enabled:bg-accent hover:enabled:text-accent-foreground shadow-none',
        link: 'text-primary underline-offset-4 hover:enabled:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-(--button-radius) px-3',
        lg: 'h-12 rounded-(--button-radius) px-8',
        icon: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
